import { Button, Header, LoadingSpinner, OtpInput } from '@components/Generic'
import { observer } from 'mobx-react'
import { CSSProperties, useContext, useEffect, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport'
import { forceRedirect } from '@methods/forceRedirect'
import '@/components/Generic/OtpInput/OtpInput.css'
import { API_URL } from '@constants/apiUrl'

const DOCUMENTS_READY_POLLING_INTERVAL = 3000

export const SignatureOtpPage = observer(() => {
  const viewport = useKeyboardAwareViewport()

  const store = useContext(StoreContext)
  const { currentScenarioId, context } = store.ScenarioState
  const { otpPage: trans } = store.TranslationsState.translations

  const otpLength = context?.otpChallengeCodeConfiguration?.length ?? 6

  const [otpValue, setOtpValue] = useState('')
  const [documentsReady, setDocumentsReady] = useState(false)
  const [isCheckingDocuments, setIsCheckingDocuments] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleProceed = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      if (currentScenarioId !== 'localTests') {
        await axiosInstance.post(
          `${API_URL}/electronic-id/workflow/sign`,
          { challengeCode: otpValue },
          { withCredentials: true }
        )
      }

      forceRedirect(`${API_URL}/electronic-id/redirect-on-finish?result=complete`)
    } catch (e) {
      devLog(e)
      setIsSubmitting(false)
    }
  }

  const handleSendCode = async () => {
    try {
      await axiosInstance.post(
        `${API_URL}/electronic-id/workflow/request-signature`,
        {},
        { withCredentials: true }
      )
    } catch (e) {
      devLog(e)
    }
  }

  const checkDocumentsReady = async () => {
    if (isCheckingDocuments) return
    setIsCheckingDocuments(true)

    try {
      const res = await axiosInstance.get<{ status: 'ready' | 'not-ready' }>(
        `${API_URL}/electronic-id/are-documents-ready-to-be-signed`,
        { withCredentials: true }
      )

      if (res.data.status === 'ready') {
        await handleSendCode()
        setDocumentsReady(true)
      }
    } catch (e) {
      devLog(e)
    } finally {
      setIsCheckingDocuments(false)
    }
  }

  useEffect(() => {
    if (currentScenarioId !== 'localTests') {
      checkDocumentsReady()
    } else {
      setDocumentsReady(true)
    }
  }, [])

  useInterval(
    () => {
      if (currentScenarioId !== 'localTests') {
        checkDocumentsReady()
      }
    },
    !documentsReady ? DOCUMENTS_READY_POLLING_INTERVAL : null
  )

  return (
    <div className="page-container">
      {!documentsReady && <LoadingSpinner />}
      {documentsReady && (
        <>
          <Header style={{ margin: '20px 0 0 0' }}>{trans.header}</Header>
          <OtpInput
            value={otpValue}
            valueLength={otpLength}
            onChange={(value) => setOtpValue(value)}
            className="otp-input__container--full-width otp-input__container--mt-20"
            alphanumeric={true}
          />
          <div
            className="otp-input__button-absolute-wrap"
            style={
              viewport?.keyboardHeight
                ? { top: 'unset', bottom: `${viewport.keyboardHeight}px` }
                : undefined
            }
          >
            <Button
              onClick={() => handleProceed()}
              style={{ '--button-width': '164px' } as CSSProperties}
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner /> : trans.verify}
            </Button>
          </div>
        </>
      )}
    </div>
  )
})
